import { useRef, useCallback, ReactNode } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface SelectableMessageProps {
  messageId: string;
  isSelected: boolean;
  isSelectionMode: boolean;
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  isFromCurrentUser: boolean;
  children: ReactNode;
}

export function SelectableMessage({
  messageId,
  isSelected,
  isSelectionMode,
  onSelect,
  onToggle,
  isFromCurrentUser,
  children,
}: SelectableMessageProps) {
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      touchStartPos.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      longPressTimerRef.current = setTimeout(() => {
        if (!isSelectionMode) {
          onSelect(messageId);
        }
      }, 500);
    },
    [isSelectionMode, messageId, onSelect]
  );

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartPos.current) return;
    const deltaX = Math.abs(e.touches[0].clientX - touchStartPos.current.x);
    const deltaY = Math.abs(e.touches[0].clientY - touchStartPos.current.y);
    // Cancel if user moves finger too much (scrolling)
    if (deltaX > 10 || deltaY > 10) {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    touchStartPos.current = null;
  }, []);

  const handleClick = useCallback(() => {
    if (isSelectionMode) {
      onToggle(messageId);
    }
  }, [isSelectionMode, messageId, onToggle]);

  return (
    <div
      className={cn(
        "relative flex items-start gap-2 transition-colors duration-150",
        isFromCurrentUser ? "flex-row-reverse" : "flex-row",
        isSelected && "bg-primary/10 -mx-3 px-3 py-1 rounded-lg"
      )}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={handleClick}
    >
      {/* Selection circle */}
      {isSelectionMode && (
        <div
          className={cn(
            "shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mt-2 transition-all",
            isSelected
              ? "bg-primary border-primary"
              : "border-muted-foreground/40 bg-background"
          )}
        >
          {isSelected && <Check size={12} className="text-primary-foreground" />}
        </div>
      )}
      
      {/* Message content */}
      <div className={cn("flex-1 min-w-0", isSelectionMode && "pointer-events-none")}>
        {children}
      </div>
    </div>
  );
}
